import React, { useMemo, useState } from "react";
import {
    Badge,
    Box,
    Button,
    Heading,
    HStack,
    Input,
    Select,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
} from "@chakra-ui/react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import {
    CollectionPin,
    DispatchJob,
    useCreateCollectionPinMutation,
    useGetCollectionPinsQuery,
    useGetDispatchJobsForPinManagementQuery,
    useResendCollectionPinMutation,
} from "./pinManagementApi";

const formatMoney = (value?: number) =>
    `MWK ${Number(value || 0).toLocaleString()}`;

const getPinStatusColorScheme = (status?: string) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "active") return "green";
    if (normalized === "used") return "blue";
    if (normalized === "expired") return "orange";
    if (normalized === "cancelled") return "red";

    return "gray";
};

const PinManagementPage: React.FC = () => {
    const { theme } = useCustomTheme();
    const toast = useToast();

    const { data: pins = [], isLoading: isPinsLoading } =
        useGetCollectionPinsQuery();

    const { data: jobs = [], isLoading: isJobsLoading } =
        useGetDispatchJobsForPinManagementQuery();

    const [createPin, { isLoading: creating }] =
        useCreateCollectionPinMutation();
    const [resendPin] = useResendCollectionPinMutation();

    const [selectedJobId, setSelectedJobId] = useState("");
    const [createdBy, setCreatedBy] = useState("");
    const [note, setNote] = useState("");

    const filteredJobs = useMemo(() => {
        return jobs.filter((job: DispatchJob) =>
            [
                "assigned",
                "arrived_at_pickup",
                "picked_up",
                "in_transit",
                "arrived_at_delivery",
                "proof_of_delivery_pending",
            ].includes(String(job.status || "")),
        );
    }, [jobs]);

    const selectedJob = useMemo(() => {
        return (
            filteredJobs.find(
                (job: DispatchJob) => String(job._id) === String(selectedJobId),
            ) || null
        );
    }, [filteredJobs, selectedJobId]);

    const handleCreate = async () => {
        if (!selectedJob) {
            toast({
                status: "warning",
                title: "Select a job first",
            });
            return;
        }

        try {
            await createPin({
                clientName: selectedJob.customerName || "",
                clientPhone: selectedJob.customerPhone || "",
                clientEmail: "",
                orderId:
                    selectedJob.sourceType === "order"
                        ? String(selectedJob.sourceId || "")
                        : "",
                deliveryId:
                    selectedJob.sourceType === "delivery"
                        ? String(selectedJob.sourceId || "")
                        : "",
                dispatchJobId: String(selectedJob._id || ""),
                assignedDriverId: String(selectedJob.assignedDriverId || ""),
                createdBy,
                note,
            }).unwrap();

            toast({
                status: "success",
                title: "PIN created",
            });

            setSelectedJobId("");
            setCreatedBy("");
            setNote("");
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to create PIN",
                description: error?.data?.message || error?.message,
            });
        }
    };

    const handleResend = async (pinId: string) => {
        try {
            await resendPin({ pinId }).unwrap();
            toast({
                status: "success",
                title: "PIN resent",
            });
        } catch (error: any) {
            toast({
                status: "error",
                title: "Failed to resend PIN",
                description: error?.data?.message || error?.message,
            });
        }
    };

    return (
        <Box p={0}>
            <Stack spacing={5}>
                <Box>
                    <Heading size="md" color={theme.text}>
                        PIN Driver Assignment
                    </Heading>
                    <Text mt={1} fontSize="sm" color={theme.textSubtle}>
                        Generate and manage PINs directly from active jobs without retyping client details.
                    </Text>
                </Box>

                <Box
                    p={4}
                    border="1px solid"
                    borderColor={theme.border}
                    borderRadius="xl"
                    bg={theme.surface}
                >
                    <Text fontWeight="700" color={theme.text} mb={4}>
                        Generate PIN from Active Job
                    </Text>

                    <Stack spacing={4}>
                        <Box>
                            <Text mb={2} fontSize="sm" fontWeight="600" color={theme.text}>
                                Select job
                            </Text>
                            <Select
                                placeholder={isJobsLoading ? "Loading jobs..." : "Choose active job"}
                                value={selectedJobId}
                                onChange={(e) => setSelectedJobId(e.target.value)}
                                bg={theme.inputBg}
                                color={theme.text}
                                borderColor={theme.inputBorder}
                                _hover={{ borderColor: theme.inputBorderFocus }}
                                _focus={{
                                    borderColor: theme.inputBorderFocus,
                                    boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                                }}
                            >
                                {filteredJobs.map((job: DispatchJob) => (
                                    <option key={job._id} value={job._id}>
                                        {`${job._id} • ${job.customerName || "Customer"} • ${job.status || "-"
                                            }`}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        {selectedJob && (
                            <Box
                                p={4}
                                border="1px solid"
                                borderColor={theme.border}
                                borderRadius="lg"
                                bg={theme.surfaceAlt}
                            >
                                <Text color={theme.text}>
                                    <b>Job ID:</b> {selectedJob._id}
                                </Text>
                                <Text color={theme.text}>
                                    <b>Customer:</b> {selectedJob.customerName || "-"}
                                </Text>
                                <Text color={theme.text}>
                                    <b>Phone:</b> {selectedJob.customerPhone || "-"}
                                </Text>
                                <Text color={theme.text}>
                                    <b>Assigned Driver:</b> {selectedJob.assignedDriverId || "-"}
                                </Text>
                                <Text color={theme.text}>
                                    <b>Status:</b> {selectedJob.status || "-"}
                                </Text>
                                <Text color={theme.text}>
                                    <b>Order Value:</b> {formatMoney(selectedJob.orderValue)}
                                </Text>
                                <Text color={theme.text}>
                                    <b>Driver Earning:</b> {formatMoney(selectedJob.driverEarning)}
                                </Text>
                                <Text fontSize="sm" color={theme.textSubtle} mt={2}>
                                    <b>From:</b> {selectedJob.pickupAddress || "-"}
                                </Text>
                                <Text fontSize="sm" color={theme.textSubtle}>
                                    <b>To:</b> {selectedJob.dropoffAddress || "-"}
                                </Text>
                            </Box>
                        )}

                        <HStack
                            align="stretch"
                            flexDirection={{ base: "column", md: "row" }}
                        >
                            <Input
                                placeholder="Created by"
                                value={createdBy}
                                onChange={(e) => setCreatedBy(e.target.value)}
                                bg={theme.inputBg}
                                color={theme.text}
                                borderColor={theme.inputBorder}
                                _placeholder={{ color: theme.inputPlaceholder }}
                                _hover={{ borderColor: theme.inputBorderFocus }}
                                _focus={{
                                    borderColor: theme.inputBorderFocus,
                                    boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                                }}
                            />
                            <Input
                                placeholder="Note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                bg={theme.inputBg}
                                color={theme.text}
                                borderColor={theme.inputBorder}
                                _placeholder={{ color: theme.inputPlaceholder }}
                                _hover={{ borderColor: theme.inputBorderFocus }}
                                _focus={{
                                    borderColor: theme.inputBorderFocus,
                                    boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                                }}
                            />
                        </HStack>

                        <Button
                            onClick={handleCreate}
                            isLoading={creating}
                            isDisabled={!selectedJob}
                            alignSelf="flex-start"
                        >
                            Generate PIN
                        </Button>
                    </Stack>
                </Box>

                <Box
                    border="1px solid"
                    borderColor={theme.border}
                    borderRadius="xl"
                    overflow="hidden"
                    bg={theme.surface}
                >
                    <Box px={4} py={3} borderBottom="1px solid" borderColor={theme.border}>
                        <Text fontWeight="700" color={theme.text}>
                            Existing PIN Assignments
                        </Text>
                        <Text fontSize="sm" color={theme.textSubtle}>
                            {pins.length} PIN assignment{pins.length === 1 ? "" : "s"}
                        </Text>
                    </Box>

                    <Box overflowX="auto">
                        <Table variant="simple" size="md">
                            <Thead bg={theme.surfaceAlt}>
                                <Tr>
                                    <Th color={theme.textMuted}>PIN</Th>
                                    <Th color={theme.textMuted}>Client</Th>
                                    <Th color={theme.textMuted}>Phone</Th>
                                    <Th color={theme.textMuted}>Status</Th>
                                    <Th color={theme.textMuted}>Driver</Th>
                                    <Th color={theme.textMuted}>Dispatch Job</Th>
                                    <Th color={theme.textMuted}>Linked Record</Th>
                                    <Th color={theme.textMuted}>Action</Th>
                                </Tr>
                            </Thead>

                            <Tbody>
                                {pins.map((pin: CollectionPin) => (
                                    <Tr key={pin._id}>
                                        <Td>
                                            <Text fontWeight="700" color={theme.text}>
                                                {pin.code}
                                            </Text>
                                        </Td>

                                        <Td>
                                            <Text fontWeight="600" color={theme.text}>
                                                {pin.clientName || "-"}
                                            </Text>
                                        </Td>

                                        <Td>
                                            <Text color={theme.text}>{pin.clientPhone || "-"}</Text>
                                        </Td>

                                        <Td>
                                            <Badge colorScheme={getPinStatusColorScheme(pin.status)}>
                                                {pin.status}
                                            </Badge>
                                        </Td>

                                        <Td>
                                            <Text color={theme.text}>{pin.assignedDriverId || "-"}</Text>
                                        </Td>

                                        <Td>
                                            <Text color={theme.text}>{pin.dispatchJobId || "-"}</Text>
                                        </Td>

                                        <Td>
                                            <Text fontSize="sm" color={theme.text}>
                                                {pin.orderId ? `Order: ${pin.orderId}` : "-"}
                                            </Text>
                                            <Text fontSize="sm" color={theme.textSubtle}>
                                                {pin.deliveryId ? `Delivery: ${pin.deliveryId}` : ""}
                                            </Text>
                                        </Td>

                                        <Td>
                                            <Button size="sm" onClick={() => handleResend(pin._id)}>
                                                Resend
                                            </Button>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>

                {(isPinsLoading || isJobsLoading) && (
                    <Text color={theme.textSubtle}>Loading...</Text>
                )}
            </Stack>
        </Box>
    );
};

export default PinManagementPage;